import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { classNames } from '~/utils/classNames';
import { ChevronDown } from 'lucide-react';

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  component?: React.ReactNode;
}

export interface DropdownProps {
  id?: string;
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
}

const dropdownVariants = {
  hidden: {
    opacity: 0,
    y: -5,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -5,
    scale: 0.95,
    transition: {
      duration: 0.15,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  (
    { id, options, value, onChange, className, placeholder = 'Select an option', disabled = false, label, size = 'md' },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const selectedOption = options.find((option) => option.value === value);

    const sizeClasses = {
      sm: 'px-2 py-1.5 text-sm',
      md: 'px-3 py-2 text-base',
      lg: 'px-4 py-2.5 text-lg',
    };

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    return (
      <div ref={dropdownRef} className={classNames('relative', className)}>
        {label && <label className="block text-sm font-medium text-alpha-elements-textSecondary mb-1.5">{label}</label>}
        <div
          className={classNames(
            'flex items-center justify-between rounded-md border transition-all duration-200',
            sizeClasses[size],
            'bg-alpha-elements-background-depth-2 border-alpha-elements-borderColor text-alpha-elements-textPrimary',
            'hover:border-alpha-elements-borderColorHover hover:bg-alpha-elements-background-depth-3',
            'focus-within:ring-2 focus-within:ring-alpha-elements-borderColorFocus focus-within:border-transparent',
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            isOpen ? 'border-alpha-elements-borderColorFocus bg-alpha-elements-background-depth-3' : ' ',
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          role="button"
          tabIndex={0}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          id={id}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {selectedOption?.icon && <span className="w-[20px] h-[20px]">{selectedOption.icon}</span>}
            <span className="truncate leading-normal">{selectedOption?.label || placeholder}</span>
          </div>
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="flex-shrink-0 ml-2 text-alpha-elements-textTertiary"
          >
            <ChevronDown size={size === 'sm' ? 14 : size === 'md' ? 16 : 18} />
          </motion.div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={ref}
              className={classNames(
                'absolute z-50 w-full mt-1 rounded-md border shadow-lg',
                'border-alpha-elements-borderColor bg-alpha-elements-background-depth-2',
                'backdrop-blur-sm backdrop-filter',
              )}
              variants={dropdownVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <ul
                className="py-1 max-h-60 overflow-auto scrollbar-thin scrollbar-thumb-alpha-elements-scrollbar-thumb scrollbar-track-transparent"
                role="listbox"
                aria-labelledby={id}
                aria-activedescendant={selectedOption?.value}
              >
                {options.map((option) => (
                  <li
                    key={option.value}
                    className={classNames(
                      'flex items-center gap-2',
                      sizeClasses[size],
                      'cursor-pointer transition-colors duration-150',
                      option.value === value
                        ? 'bg-alpha-elements-item-backgroundActive text-alpha-elements-item-contentAccent'
                        : 'text-alpha-elements-textPrimary hover:bg-alpha-elements-item-backgroundHover',
                      option.disabled ? 'opacity-50 cursor-not-allowed' : '',
                    )}
                    role="option"
                    aria-selected={option.value === value}
                    onClick={() => {
                      if (!option.disabled) {
                        onChange(option.value);
                        setIsOpen(false);
                      }
                    }}
                  >
                    {option.icon && <span className="w-[20px] h-[20px]">{option.icon}</span>}
                    <span className="truncate leading-normal">{option.label}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

Dropdown.displayName = 'Dropdown';
